// src/pages/SetPermission.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import toastr from "toastr";
import { authFetch } from "../utils/authFetch";

const API = "/api/v1";
const toast = (m, t = "success") => {
  toastr.options = { closeButton: true, progressBar: true, timeOut: 3000 };
  toastr[t](m);
};

/* ───── Child for single category ───── */
function CategoryBlock({ cat, slugs, sel, onToggle, onToggleAll }) {
  const refAll = useRef();
  const allChecked = Object.values(sel).every(Boolean);
  const someChecked = Object.values(sel).some(Boolean);

  useEffect(() => {
    if (refAll.current) refAll.current.indeterminate = !allChecked && someChecked;
  }, [allChecked, someChecked]);

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">{cat}</h6>
        <label className="mb-0">
          <input
            type="checkbox"
            ref={refAll}
            checked={allChecked}
            onChange={(e) => onToggleAll(cat, e.target.checked)}
          />{" "}
          Select All
        </label>
      </div>
      <hr />
      {slugs.map((slug) => (
        <label key={slug} className="me-3 d-inline-block">
          <input
            type="checkbox"
            checked={sel[slug] || false}
            onChange={(e) => onToggle(cat, slug, e.target.checked)}
          />{" "}
          {slug}
        </label>
      ))}
    </div>
  );
}

/* ───── Parent page ───── */
export default function SetPermission() {
  const { id: userId } = useParams();

  const [permMap, setPermMap] = useState({});     // {type:[slug]}
  const [permSel, setPermSel] = useState({});     // {type:{slug:true}}
  const [slug2Id, setSlug2Id] = useState({});     // slug → permission_id
  const [roleId,  setRoleId]  = useState("");     // user role
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  /* ───── fetch once ───── */
  useEffect(() => {
    (async () => {
      try {
        /* 1️⃣ master list (full objects) -> slug→id map */
        const fullRes = await authFetch(`${API}/permission/list`);
        const fullList = await fullRes.json();               // [{id,slug,type}, …]
        const s2id = {};
        fullList.forEach((p) => (s2id[p.slug] = p.id));
        setSlug2Id(s2id);

        /* 2️⃣ grouped list for UI */
        const groupedRes = await authFetch(`${API}/permission/list/grouped`);
        const grouped = await groupedRes.json();

        /* 3️⃣ user‑current role & perms */
        const profRes = await authFetch(`${API}/member/profile/${userId}`);
        const prof    = await profRes.json();
        setRoleId(String(prof.user.role_id || ""));

        let userPerms = {};
        try {
          const r = await authFetch(`${API}/permission/userpermission/${userId}`);
          if (r.ok) userPerms = await r.json();      // same structure as grouped selection
        } catch (_) {}

        /* 4️⃣ build selection state */
        const sel = {};
        Object.entries(grouped).forEach(([cat, slugs]) => {
          sel[cat] = {};
          slugs.forEach((s) => (sel[cat][s] = !!userPerms?.[cat]?.[s]));
        });

        setPermMap(grouped);
        setPermSel(sel);
      } catch (e) {
        toast("Cannot load permissions", "error");
        console.log(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  /* ───── helpers ───── */
  const toggle = (cat, slug, val) =>
    setPermSel((p) => ({ ...p, [cat]: { ...p[cat], [slug]: val } }));

  const toggleAll = (cat, val) =>
    setPermSel((p) => ({
      ...p,
      [cat]: Object.fromEntries(Object.keys(p[cat]).map((k) => [k, val])),
    }));

  /* ───── save handler ───── */
  const save = async () => {
    setSaving(true);
    try {
      /* selected slug list -> permission_ids */
      const ids = [];
      Object.values(permSel).forEach((obj) => {
        Object.entries(obj).forEach(([slug, v]) => {
          if (v && slug2Id[slug]) ids.push(slug2Id[slug]);
        });
      });

      const body = {
        user_id: userId,
        role_id: roleId,
        permission_ids: ids,
      };

      const res = await authFetch(`${API}/permission/userpermission/save`, {
        method: "POST",
        // headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Save failed");
      toast(`Permissions saved (${d.count})`);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  /* ───── UI ───── */
  if (loading) return <div className="p-3">Loading…</div>;

  return (
    <div className="page-wrapper">
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="card radius-15">
            <div className="card-body">
              <h5 className="mb-3">User Set Permission</h5>

              {Object.entries(permMap).map(([cat, slugs]) => (
                <CategoryBlock
                  key={cat}
                  cat={cat}
                  slugs={slugs}
                  sel={permSel[cat]}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                />
              ))}

              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save Permissions"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
